// Fixed card grid for the phones heatmap. Measures its own box with a
// ResizeObserver and sizes every card so the whole fleet fits the
// viewport (computeLayout). The card count is the fleet size — stable
// between polls — so cols/size only change on a window resize, never
// per tick: cards change state in place, the grid never reshuffles.

import { useEffect, useRef, useState } from 'react'
import { cardColors, computeLayout, gridMaxes } from '../../lib/heatmapCells'
import type { PhoneCell } from '../../lib/heatmapCells'
import { PhoneCard } from './PhoneCard'

interface PhoneGridProps {
  cells: PhoneCell[]
  /** Called with a phone id when its card is activated. Omitted → cards
   * stay non-interactive. */
  onSelect?: (id: string) => void
}

const GAP = 8

export function PhoneGrid({ cells, onSelect }: PhoneGridProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect
      setBox({ w: cr.width, h: cr.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const layout = computeLayout(box.w, box.h, GAP, cells.length)
  const { maxDuration, maxScore } = gridMaxes(cells)

  return (
    // overflow-y-auto: a normal fleet fits (no scroll); only a huge fleet,
    // where computeLayout floors the card size, scrolls — and only this
    // board area, never the page.
    <div
      ref={ref}
      className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto"
      role="list"
      aria-label="Сетка телефонов"
    >
      <div
        style={{
          display: 'grid',
          gap: GAP,
          gridTemplateColumns: `repeat(${layout.cols}, ${layout.size}px)`,
          placeContent: 'center',
        }}
      >
        {cells.map((cell) => (
          <PhoneCard
            key={cell.id}
            cell={cell}
            size={layout.size}
            colors={cardColors(cell, maxDuration, maxScore)}
            onSelect={onSelect ? () => onSelect(cell.id) : undefined}
          />
        ))}
      </div>
    </div>
  )
}
